import React, { useState, useEffect, useRef } from "react";
import { FaMicrophone, FaHistory, FaSpinner, FaStop, FaMoon, FaSun, FaCopy, FaDownload, FaSignOutAlt, FaUserCircle, FaTrash, FaFileExport, FaEdit, FaFilePdf } from "react-icons/fa";
import { IoMdInformationCircle } from "react-icons/io";
import OpenAI from 'openai';
import lamejs from 'lamejs';
import Modal from 'react-modal';
import { jsPDF } from 'jspdf';
import { auth, db, onAuthStateChanged } from '../firebase';
import { signOut } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  Timestamp,
  orderBy 
} from 'firebase/firestore';

// Configuração do Modal para acessibilidade
Modal.setAppElement('#root');

// Initialize OpenAI client with error handling
const initializeOpenAI = () => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  if (!apiKey) {
    console.error('OpenAI API Key não encontrada. Por favor, configure a variável de ambiente REACT_APP_OPENAI_API_KEY.');
    return null;
  }
  
  try {
    return new OpenAI({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true
    });
  } catch (error) {
    console.error('Erro ao inicializar OpenAI:', error);
    return null;
  }
};

const openai = initializeOpenAI();

const MeetingTranscription = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcriptText, setTranscriptText] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedMimeType, setSelectedMimeType] = useState('audio/wav');
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedTranscription, setEditedTranscription] = useState('');
  const [editedMeetingNotes, setEditedMeetingNotes] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    // Aplicar tema escuro/claro
    document.body.className = darkMode ? 'dark' : 'light';
    return () => {
      document.body.className = '';
    };
  }, [darkMode]);

  // Função para analisar níveis de áudio
  const analyzeAudio = (stream) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
    }

    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);

    const updateAudioLevel = () => {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      setAudioLevel(average);
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    };

    updateAudioLevel();
  };

  // Função para copiar texto
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError("Erro ao copiar texto");
    }
  };

  // Função para exportar como arquivo
  const exportAsFile = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Validate API key
  const validateApiKey = (key) => {
    return key && 
           typeof key === 'string' && 
           key.trim().length > 0 && 
           key.startsWith('sk-');
  };

  // Convert blob to MP3
  const convertBlobToMp3 = async (blob) => {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          const offlineContext = new OfflineAudioContext(
            audioBuffer.numberOfChannels,
            audioBuffer.length,
            audioBuffer.sampleRate
          );

          const source = offlineContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(offlineContext.destination);
          source.start();

          const renderedBuffer = await offlineContext.startRendering();

          // Safer MP3 encoding with fallback
          const mp3Data = [];
          try {
            // Ensure lamejs is available and MPEGMode is defined
            if (typeof lamejs !== 'undefined' && lamejs.MPEGMode) {
              const mp3encoder = new lamejs.Mp3Encoder(
                1,  // mono
                audioBuffer.sampleRate, 
                128  // bitrate
              );

              const samples = renderedBuffer.getChannelData(0);
              const sampleBlockSize = 1152;
              for (let i = 0; i < samples.length; i += sampleBlockSize) {
                const sampleChunk = samples.subarray(i, i + sampleBlockSize);
                
                // Convert float samples to 16-bit PCM
                const int16Samples = new Int16Array(sampleChunk.length);
                for (let j = 0; j < sampleChunk.length; j++) {
                  int16Samples[j] = Math.max(-32768, Math.min(32767, sampleChunk[j] * 32768.0));
                }

                const mp3buf = mp3encoder.encodeBuffer(int16Samples);
                if (mp3buf.length > 0) {
                  mp3Data.push(mp3buf);
                }
              }
              
              const mp3bufEnd = mp3encoder.flush();
              if (mp3bufEnd.length > 0) {
                mp3Data.push(mp3bufEnd);
              }
            } else {
              // Fallback to creating an MP3 via server or alternative method
              console.warn('lamejs not fully supported, using alternative conversion');
              throw new Error('MP3 conversion not supported');
            }
          } catch (encodingError) {
            console.error('MP3 encoding error:', encodingError);
            
            // Fallback to creating a WAV file if MP3 fails
            const wavBlob = bufferToWave(renderedBuffer, renderedBuffer.length);
            const wavFile = new File([wavBlob], 'recording.wav', { type: 'audio/wav' });
            resolve(wavFile);
            return;
          }

          const blob = new Blob(mp3Data, { type: 'audio/mp3' });
          const mp3File = new File([blob], 'recording.mp3', { type: 'audio/mp3' });
          
          resolve(mp3File);
        } catch (error) {
          console.error('Conversion error:', error);
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  };

  // Utility function to convert AudioBuffer to WAV
  const bufferToWave = (abuffer, len) => {
    const numberOfChannels = abuffer.numberOfChannels;
    const sampleRate = abuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const wav = new ArrayBuffer(44 + abuffer.length * bytesPerSample);
    const view = new DataView(wav);
    
    // WAV header
    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + abuffer.length * bytesPerSample, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, abuffer.length * bytesPerSample, true);
    
    // Write audio data
    const offset = 44;
    const bufferData = new Float32Array(abuffer.length);
    abuffer.copyFromChannel(bufferData, 0);
    
    for (let i = 0; i < abuffer.length; i++) {
      const sample = Math.max(-1, Math.min(1, bufferData[i]));
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset + i * bytesPerSample, value, true);
    }
    
    return wav;
  };

  // Função para detectar navegador e sistema operacional
  const detectBrowserAndPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const vendor = navigator.vendor.toLowerCase();
    
    const isSafari = /safari/.test(userAgent) && /apple/.test(vendor) && !/chrome/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && /google/.test(vendor);
    const isEdge = /edg/.test(userAgent);
    const isMac = /macintosh/.test(userAgent);
    const isWindows = /windows/.test(userAgent);

    return { isSafari, isChrome, isEdge, isMac, isWindows };
  };

  // Selecionar MIME type suportado
  useEffect(() => {
    const mimeTypes = [
      'audio/webm',
      'audio/mp4',
      'audio/wav',
      'audio/ogg'
    ];

    const supported = mimeTypes.find(type => MediaRecorder.isTypeSupported(type));
    if (supported) {
      setSelectedMimeType(supported);
    }
  }, []);

  // Combinar streams de áudio
  const combineAudioStreams = (systemStream, microphoneStream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const destination = audioContext.createMediaStreamDestination();

    if (systemStream && systemStream.getAudioTracks().length > 0) {
      const systemSource = audioContext.createMediaStreamSource(new MediaStream([systemStream.getAudioTracks()[0]]));
      systemSource.connect(destination);
    }

    if (microphoneStream && microphoneStream.getAudioTracks().length > 0) {
      const micSource = audioContext.createMediaStreamSource(new MediaStream([microphoneStream.getAudioTracks()[0]]));
      micSource.connect(destination);
    }

    return destination.stream;
  };

  // Capturar áudio universal
  const captureUniversalAudio = async () => {
    try {
      const detection = detectBrowserAndPlatform();

      // Configurar constraints para captura de áudio
      const audioConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      };

      // Capturar áudio do microfone
      const microphoneStream = await navigator.mediaDevices.getUserMedia(audioConstraints);

      let systemStream = null;

      // Capturar áudio do sistema (se disponível)
      if (detection.isChrome || detection.isEdge) {
        try {
          // Importante: precisamos pedir vídeo também para capturar o áudio
          systemStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              width: 1,
              height: 1,
              frameRate: 1
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              suppressLocalAudioPlayback: false
            }
          });

          // Se temos áudio, podemos descartar o vídeo
          if (systemStream.getAudioTracks().length > 0) {
            systemStream.getVideoTracks().forEach(track => track.stop());
            // Criar nova stream apenas com áudio
            systemStream = new MediaStream(systemStream.getAudioTracks());
          }
        } catch (err) {
          console.warn('⚠️ Não foi possível capturar áudio do sistema:', err);
        }
      }

      // Combinar as streams
      const combinedStream = combineAudioStreams(systemStream, microphoneStream);

      // Registrar status final
      return combinedStream;
    } catch (error) {
      console.error('❌ Erro ao capturar áudio:', error);
      throw new Error('Não foi possível capturar o áudio: ' + error.message);
    }
  };

  // Método de gravação unificado
  const toggleRecording = async () => {
    try {
      if (!isRecording) {
        await startRecording();
      } else {
        await stopRecording();
      }
    } catch (error) {
      console.error('Erro ao alternar gravação:', error);
      setError('Não foi possível iniciar/parar a gravação');
    }
  };

  // Iniciar gravação
  const startRecording = async () => {
    try {
      setIsRecording(true);
      setIsProcessing(false);
      setError(null);
      setTranscriptText('');
      setNotes('');
      audioChunksRef.current = [];

      // Capturar áudio universal
      const stream = await captureUniversalAudio();
      
      // Configurar MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType: selectedMimeType 
      });
      
      mediaRecorderRef.current = mediaRecorder;

      // Evento para coletar chunks de áudio
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Evento quando a gravação para
      mediaRecorder.onstop = async () => {
        // Criar blob de áudio
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: selectedMimeType 
        });

        // Transcrever áudio
        await handleTranscription(audioBlob);
      };

      // Iniciar gravação
      mediaRecorder.start();

      // Configurar análise de nível de áudio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Função para medir nível de áudio
      const measureAudioLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const averageLevel = dataArray.reduce((a, b) => a + b) / bufferLength;
        setAudioLevel(averageLevel);
        
        animationFrameRef.current = requestAnimationFrame(measureAudioLevel);
      };

      measureAudioLevel();
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      setError('Não foi possível iniciar a gravação. Verifique as permissões.');
      setIsRecording(false);
    }
  };

  // Parar gravação
  const stopRecording = async () => {
    try {
      // Parar gravação de mídia
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }

      // Parar análise de áudio
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Fechar contexto de áudio
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Parar todas as tracks de mídia
      const tracks = mediaRecorderRef.current?.stream?.getTracks() || [];
      tracks.forEach(track => track.stop());

      // Resetar estados
      setIsRecording(false);
      setAudioLevel(0);
      
      // Processamento será feito no evento onstop do MediaRecorder
    } catch (error) {
      console.error('Erro ao parar gravação:', error);
      setError('Erro ao parar a gravação');
      setIsRecording(false);
    }
  };

  // Converter áudio para WAV
  const convertToWav = async (webmBlob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioData = await audioContext.decodeAudioData(e.target.result);
          
          // Criar buffer de áudio WAV
          const wavBuffer = audioContext.createBuffer(
            audioData.numberOfChannels,
            audioData.length,
            audioData.sampleRate
          );
          
          // Copiar dados do áudio
          for (let channel = 0; channel < audioData.numberOfChannels; channel++) {
            wavBuffer.copyToChannel(audioData.getChannelData(channel), channel);
          }
          
          // Converter para WAV
          const wavBlob = await audioBufferToWav(wavBuffer);
          resolve(new Blob([wavBlob], { type: 'audio/wav' }));
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(webmBlob);
    });
  };

  // Converter AudioBuffer para WAV
  const audioBufferToWav = (buffer) => {
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const wav = new ArrayBuffer(44 + buffer.length * bytesPerSample);
    const view = new DataView(wav);
    
    // WAV header
    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + buffer.length * bytesPerSample, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, buffer.length * bytesPerSample, true);
    
    // Write audio data
    const offset = 44;
    const bufferData = new Float32Array(buffer.length);
    buffer.copyFromChannel(bufferData, 0);
    
    for (let i = 0; i < buffer.length; i++) {
      const sample = Math.max(-1, Math.min(1, bufferData[i]));
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset + i * bytesPerSample, value, true);
    }
    
    return wav;
  };

  // Transcrição do áudio
  const transcribeAudio = async (audioBlob) => {
    try {
      if (!openai) {
        throw new Error('Cliente OpenAI não inicializado. Verifique sua chave de API.');
      }

      console.log('🔊 Iniciando transcrição do áudio...');
      
      // Criar FormData com o arquivo de áudio
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.wav');
      formData.append('model', 'whisper-1');
      formData.append('language', 'pt');
      formData.append('response_format', 'json');
      
      // Fazer a requisição para a API
      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro na API: ${response.status} - ${errorData.error?.message || 'Erro desconhecido'}`);
      }

      const data = await response.json();
      console.log('📝 Resposta da OpenAI:', data);

      if (!data.text) {
        throw new Error('Transcrição vazia recebida da API');
      }

      return data.text;

    } catch (error) {
      console.error('❌ Erro na transcrição:', error);
      throw error;
    }
  };

  // Função para processar transcrição com estruturação avançada
  const processTranscription = async (rawTranscription) => {
    try {
      // Verificar se já está estruturado
      if (typeof rawTranscription !== 'string') {
        return rawTranscription;
      }

      // Chamada para OpenAI para estruturar a transcrição
      const response = await openai.chat.completions.create({
        model: "gpt-3.5-turbo-16k",
        messages: [
          {
            role: "system", 
            content: `Você é um assistente que formata transcrições de reuniões de forma clara e estruturada.
            
Regras:
- Mantenha o texto EXATAMENTE como foi transcrito
- Identifique diferentes participantes como [Participante 1], [Participante 2], etc.
- Não invente ou adicione informações que não estejam no texto original
- Mantenha a ordem cronológica da conversa
- Separe falas de diferentes participantes
- Use formatação legível

Exemplo:
Entrada: "Oi, tudo bem? Vamos discutir o projeto. Acho que precisamos de mais tempo."
Saída:
[Participante 1]: Oi, tudo bem? Vamos discutir o projeto. Acho que precisamos de mais tempo.`
          },
          {
            role: "user", 
            content: rawTranscription
          }
        ],
        max_tokens: 4000,
        temperature: 0.2
      });

      // Extrair texto estruturado da resposta
      const structuredTranscription = response.choices[0].message.content.trim();

      return structuredTranscription;
    } catch (error) {
      console.error('Erro ao processar transcrição:', error);
      // Caso falhe, retorna a transcrição original
      return rawTranscription;
    }
  };

  // Generate meeting notes from transcription
  const generateMeetingNotes = async (transcriptionText) => {
    try {
      if (!openai) {
        throw new Error('Cliente OpenAI não inicializado. Verifique sua chave de API.');
      }

      // Log de depuração
      console.log('Gerando resumo para transcrição:', transcriptionText);

      // Verificar se a transcrição tem conteúdo
      if (!transcriptionText || transcriptionText.trim() === '') {
        console.warn('Transcrição vazia. Não será gerado resumo.');
        setNotes('Não foi possível gerar resumo. Transcrição vazia.');
        return 'Sem informações disponíveis para resumir.';
      }

      const response = await openai.chat.completions.create({
        model: "gpt-3.5-turbo-16k",
        messages: [
          {
            role: "system",
            content: `Você é um assistente especializado em análise e resumo de conteúdo. 
            Sua tarefa é criar um resumo inteligente e contextualizado do texto fornecido:

            Diretrizes Gerais:
            1. Identifique o tipo e contexto do conteúdo automaticamente
            2. Adapte o resumo ao tipo de conteúdo detectado:
               - Se for uma reunião: resuma pontos principais, decisões, ações
               - Se for uma conversa casual: extraia ideias principais
               - Se for uma discussão técnica: destaque conceitos-chave
               - Se for uma narrativa: resuma pontos principais

            Requisitos para o Resumo:
            - Seja conciso (máximo 300 palavras)
            - Mantenha a essência e o tom original do texto
            - Destaque informações mais relevantes
            - Use uma estrutura clara e legível
            - Corrija erros gramaticais e de pontuação
            - Preserve o contexto original

            Se o conteúdo for muito curto ou irrelevante, indique isso claramente.`
          },
          {
            role: "user",
            content: `Por favor, analise e resuma o seguinte conteúdo:
${transcriptionText}`
          }
        ],
        max_tokens: 1000,
        temperature: 0.6,
      });

      // Extrair notas geradas
      const generatedNotes = response.choices[0].message.content.trim();

      // Verificar se o resumo tem conteúdo significativo
      if (generatedNotes.includes('conteúdo muito curto') || 
          generatedNotes.length < 50) {
        const fallbackNotes = 'Não foi possível gerar um resumo detalhado. O conteúdo não contém informações suficientes para análise.';
        
        console.warn('Resumo gerado não tem conteúdo significativo');
        setNotes(fallbackNotes);
        return fallbackNotes;
      }

      // Log de depuração
      console.log('Resumo gerado:', generatedNotes);

      // Atualizar estado local
      setNotes(generatedNotes);

      // Retornar notas para uso posterior
      return generatedNotes;
    } catch (error) {
      console.error('Erro ao gerar o resumo:', error);
      const errorNotes = 'Erro ao gerar resumo. Por favor, tente novamente.';
      setNotes(errorNotes);
      return errorNotes;
    }
  };

  // Função para lidar com a transcrição do áudio
  const handleTranscription = async (audioBlob) => {
    try {
      setIsProcessing(true);
      setError(null);
      setTranscriptText('');
      setNotes('');

      // Transcrever áudio
      const transcription = await transcribeAudio(audioBlob);
      
      if (!transcription) {
        throw new Error('Não foi possível transcrever o áudio');
      }

      // Processar transcrição
      const structuredTranscription = await processTranscription(transcription);
      setTranscriptText(structuredTranscription);

      // Gerar ata/notas
      const meetingNotes = await generateMeetingNotes(structuredTranscription);
      setNotes(meetingNotes);

      // Salvar no histórico
      await addToHistory(structuredTranscription, meetingNotes);
    } catch (error) {
      console.error('❌ Erro ao processar áudio:', error);
      setError('Erro ao processar o áudio: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Modificar função de salvar transcrição para usar processamento
  const addToHistory = async (transcription, notes) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const newEntry = {
        userId: user.uid,
        transcription: transcription,
        meetingNotes: notes,
        meetingTitle: `Reunião ${new Date().toLocaleDateString()}`,
        createdAt: Timestamp.now(),
        timestamp: Timestamp.now() // Mantive timestamp por compatibilidade
      };

      const docRef = await addDoc(collection(db, 'transcriptions'), newEntry);
      
      // Atualizar estado local
      setHistory(prevHistory => [
        { id: docRef.id, ...newEntry },
        ...prevHistory
      ]);
    } catch (error) {
      console.error('Erro ao salvar transcrição:', error);
    }
  };

  // Função para buscar histórico do Firestore
  const fetchUserHistory = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const historyRef = collection(db, 'transcriptions');
      const q = query(
        historyRef, 
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const userHistory = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });

      setHistory(userHistory);
      setIsLoadingHistory(false);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error.message);
      setIsLoadingHistory(false);
    }
  };

  // Função para excluir transcrição
  const handleDeleteTranscription = async (transcriptionId) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Excluir documento no Firestore
      await deleteDoc(doc(db, 'transcriptions', transcriptionId));
      
      // Atualizar histórico local removendo o documento excluído
      setHistory(prevHistory => 
        prevHistory.filter(item => item.id !== transcriptionId)
      );

      // Opcional: Adicionar feedback de exclusão
      console.log(`Transcrição ${transcriptionId} excluída com sucesso`);
    } catch (error) {
      console.error('Erro ao excluir transcrição:', error);
      // Opcional: Adicionar tratamento de erro para o usuário
      alert('Não foi possível excluir a transcrição. Tente novamente.');
    }
  };

  // Função para exportar transcrição como texto
  const handleExportToText = (transcription, title) => {
    const meetingNotes = transcription.meetingNotes || 
                          transcription.notes || 
                          transcription.transcriptNotes || 
                          'Sem ata de reunião';
    
    const exportContent = `
Título da Reunião: ${title || 'Sem título'}

Transcrição:
${transcription.transcription || transcription.transcriptText || 'Sem transcrição'}

Ata da Reunião:
${meetingNotes}
    `;

    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'transcricao'}_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Função para exportar transcrição como PDF
  const handleExportToPDF = (transcription, title) => {
    const doc = new jsPDF();
    
    const meetingNotes = transcription.meetingNotes || 
                          transcription.notes || 
                          transcription.transcriptNotes || 
                          'Sem ata de reunião';

    // Configurações de estilo
    doc.setFontSize(16);
    doc.text(`Título da Reunião: ${title || 'Sem título'}`, 10, 10);

    doc.setFontSize(12);
    
    // Transcrição
    doc.text('Transcrição:', 10, 20);
    const transcriptionLines = doc.splitTextToSize(
      transcription.transcription || transcription.transcriptText || 'Sem transcrição', 
      190
    );
    doc.text(transcriptionLines, 10, 30);

    // Ata da Reunião
    const startY = 30 + (transcriptionLines.length * 10);
    doc.text('Ata da Reunião:', 10, startY);
    const notesLines = doc.splitTextToSize(meetingNotes, 190);
    doc.text(notesLines, 10, startY + 10);

    // Salvar PDF
    doc.save(`${title || 'transcricao'}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Modal de edição de transcrição
  const EditTranscriptionModal = () => {
    // Estado local para gerenciar edições sem afetar o estado global imediatamente
    const [localTitle, setLocalTitle] = useState(editedTitle);
    const [localTranscription, setLocalTranscription] = useState(editedTranscription);
    const [localMeetingNotes, setLocalMeetingNotes] = useState(editedMeetingNotes);

    // Função de salvamento com gerenciamento de estado local
    const handleSave = async () => {
      try {
        // Atualizar documento no Firestore
        const docRef = doc(db, 'transcriptions', selectedTranscription.id);
        await updateDoc(docRef, {
          meetingTitle: localTitle,
          transcription: localTranscription,
          meetingNotes: localMeetingNotes
        });

        // Atualizar estado global
        setEditedTitle(localTitle);
        setEditedTranscription(localTranscription);
        setEditedMeetingNotes(localMeetingNotes);

        // Atualizar histórico local
        setHistory(prevHistory => 
          prevHistory.map(item => 
            item.id === selectedTranscription.id 
              ? {
                  ...item, 
                  meetingTitle: localTitle,
                  transcription: localTranscription,
                  meetingNotes: localMeetingNotes
                } 
              : item
          )
        );

        // Fechar modal
        setIsEditModalOpen(false);
      } catch (error) {
        console.error('Erro ao salvar edições:', error);
        alert('Não foi possível salvar as alterações.');
      }
    };

    return (
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#1f2937',
            color: '#fff',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)'
          }
        }}
        contentLabel="Editar Transcrição"
      >
        <div className="space-y-4">
          <h2 className="text-xl font-bold mb-4 text-white">Editar Transcrição</h2>
          
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Título da Reunião
            </label>
            <input
              type="text"
              value={localTitle}
              onChange={(e) => {
                setLocalTitle(e.target.value);
              }}
              className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Transcrição */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Transcrição
            </label>
            <textarea
              value={localTranscription}
              onChange={(e) => {
                setLocalTranscription(e.target.value);
              }}
              rows={6}
              className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Ata/Resumo */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Resumo da Reunião
            </label>
            <textarea
              value={localMeetingNotes}
              onChange={(e) => {
                setLocalMeetingNotes(e.target.value);
              }}
              rows={6}
              className="w-full p-2 bg-gray-800 text-white border border-gray-700 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500"
            >
              Salvar
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Abrir modal de edição
  const openEditModal = (transcription) => {
    // Usar um estado de ref para evitar múltiplas aberturas
    if (isEditModalOpen) {
      // Se já estiver aberto, feche primeiro
      setIsEditModalOpen(false);
      
      // Pequeno delay para garantir que o modal seja fechado
      setTimeout(() => {
        setSelectedTranscription(transcription);
        setEditedTitle(transcription.meetingTitle || '');
        setEditedTranscription(transcription.transcription || transcription.transcriptText || '');
        setEditedMeetingNotes(
          transcription.meetingNotes || 
          transcription.notes || 
          transcription.transcriptNotes || 
          ''
        );
        setIsEditModalOpen(true);
      }, 100);
    } else {
      // Se não estiver aberto, abra normalmente
      setSelectedTranscription(transcription);
      setEditedTitle(transcription.meetingTitle || '');
      setEditedTranscription(transcription.transcription || transcription.transcriptText || '');
      setEditedMeetingNotes(
        transcription.meetingNotes || 
        transcription.notes || 
        transcription.transcriptNotes || 
        ''
      );
      setIsEditModalOpen(true);
    }
  };

  // Renderização do histórico
  const HistorySection = () => {
    if (isLoadingHistory) {
      return (
        <div className="flex justify-center items-center py-8">
          <FaSpinner className="animate-spin text-2xl text-blue-500" />
        </div>
      );
    }

    if (!showHistory) {
      return null;
    }

    return (
      <div className="mt-6 space-y-4">
        <h2 className="text-xl font-semibold mb-4">Histórico de Transcrições</h2>
        {history.length === 0 ? (
          <p className="text-gray-500 italic">Nenhuma transcrição encontrada</p>
        ) : (
          history.map((transcription) => (
            <div 
              key={transcription.id} 
              className="bg-gray-800 p-4 rounded-lg shadow-md hover:bg-gray-700 transition-colors"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-bold text-white">
                  {transcription.meetingTitle || 'Transcrição sem título'}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openEditModal(transcription)}
                    className="text-blue-400 hover:text-blue-300"
                    title="Editar transcrição"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleExportToText(transcription.transcription, transcription.meetingTitle)}
                    className="text-green-400 hover:text-green-300"
                    title="Exportar como texto"
                  >
                    <FaFileExport />
                  </button>
                  <button
                    onClick={() => handleExportToPDF(transcription.transcription, transcription.meetingTitle)}
                    className="text-red-400 hover:text-red-300"
                    title="Exportar como PDF"
                  >
                    <FaFilePdf />
                  </button>
                  <button
                    onClick={() => handleDeleteTranscription(transcription.id)}
                    className="text-red-500 hover:text-red-400"
                    title="Excluir transcrição"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              <p className="text-gray-300 line-clamp-3">
                {transcription.transcription || 'Sem conteúdo'}
              </p>
              <div className="mt-2 text-sm text-gray-500">
                {new Date(transcription.timestamp?.seconds * 1000).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // Carregar histórico quando o componente montar
  useEffect(() => {
    fetchUserHistory();
  }, []);

  // Recarregar histórico quando o usuário mudar
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserHistory();
      } else {
        setHistory([]); // Limpar histórico se não houver usuário
      }
    });

    // Limpar o listener quando o componente desmontar
    return () => unsubscribe();
  }, []);

  // Função de logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Informações do usuário
  const user = auth.currentUser;

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="flex justify-between items-center mb-4">
        {/* Informações do usuário */}
        <div className="flex items-center gap-3">
          {user?.photoURL ? (
            <img 
              src={user.photoURL} 
              alt="Foto do usuário" 
              className="w-10 h-10 rounded-full border-2 border-blue-500"
            />
          ) : (
            <FaUserCircle className="text-4xl text-gray-500" />
          )}
          <div>
            <p className="text-white font-semibold">
              {user?.displayName || 'Usuário'}
            </p>
            <p className="text-sm text-gray-400">
              {user?.email || 'email não disponível'}
            </p>
          </div>
        </div>

        {/* Botão de logout */}
        <button 
          onClick={handleLogout}
          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
        >
          <FaSignOutAlt /> Sair
        </button>
      </div>
      <div className="max-w-4xl mx-auto bg-gray-800 shadow-xl rounded-lg p-8 text-gray-100">
        <h1 className="text-3xl font-bold text-center mb-8 text-gray-100">
          Transcrição de Reunião
        </h1>
        
        <div className="space-y-8">
          <div className="flex justify-center space-x-4">
          </div>

          {/* Controles principais */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={toggleRecording}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg text-white transition-all ${
                isRecording 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              disabled={isProcessing}
            >
              {isRecording ? (
                <>
                  <FaStop /> Parar Gravação
                </>
              ) : (
                <>
                  <FaMicrophone /> Iniciar Gravação
                </>
              )}
            </button>

            {isRecording && (
              <div className="flex-1 flex items-center">
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all duration-100"
                    style={{ width: `${(audioLevel / 255) * 100}%` }}
                  />
                </div>
              </div>
            )}

          </div>

          {/* Área de status e progresso */}
          {isProcessing && (
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-4">
              <FaSpinner className="animate-spin" />
              <span>Processando áudio...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
              <div className="flex items-center gap-2">
                <IoMdInformationCircle className="text-xl" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Área de transcrição */}
          <div className="bg-gray-900/50 p-8 rounded-lg border border-gray-700 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-200">Transcrição Atual</h2>
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700 min-h-[200px]">
              {transcriptText ? (
                <p className="whitespace-pre-wrap text-gray-300">{transcriptText}</p>
              ) : (
                <p className="text-gray-500 italic">Nenhuma transcrição ainda...</p>
              )}
            </div>
          </div>

          {/* Área de resumo/ata */}
          <div className="bg-gray-900/50 p-8 rounded-lg border border-gray-700 shadow-lg mt-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-200">Resumo da Reunião</h2>
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700 min-h-[200px]">
              {notes ? (
                <p className="whitespace-pre-wrap text-gray-300">{notes}</p>
              ) : (
                <p className="text-gray-500 italic">Nenhuma ata gerada ainda...</p>
              )}
            </div>
          </div>

          {/* Histórico */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-6 py-3 rounded-lg font-semibold bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200 shadow-lg shadow-gray-700/30"
          >
            {showHistory ? 'Ocultar Histórico' : 'Mostrar Histórico'}
          </button>

          <HistorySection />

          {/* Toast de feedback */}
          {copied && (
            <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg">
              Copiado com sucesso!
            </div>
          )}
        </div>

        {/* Modal de edição de transcrição */}
        <EditTranscriptionModal />
      </div>
    </div>
  );
};

export default MeetingTranscription;
